import React, { useState } from "react";
import { observer } from "mobx-react";
import { toJS } from "mobx";
import Tooltip from "ui/Tooltip/Tooltip";
import buttonStyles from "ui/Button/Button.module.scss";
import ProductToast from "common/Notifications/ProductToast/ProductToast";
import AddToCartModalWithPrices from "common/AddToCart/components/AddToCartModalWithPrices/AddToCartModalWithPrices";
import Basket from "assets/icons/basket.svg";
import AttentionSignSmall from "assets/icons/attention-sign-small.svg";
import AttentionSign from "assets/icons/attention-sign.svg";
import { useStore } from "store";
import { useBreakpoint } from "helpers/hooks";

import { cn } from "helpers/style";
import styles from "./AddToCartSingle.module.scss";

type Props = {
  product: Entity.Product;
  price: Entity.Product.Price;
  analyticsMeta?: Entity.ProductAnalyticsMeta;
  hideCartToast?: boolean;
  onAddProduct?: () => void;
  className?: string;
};

const AddToCartSingle: React.FC<Props> = ({
  product,
  price,
  analyticsMeta,
  hideCartToast,
  onAddProduct,
  className,
}: Props) => {
  const { cart, notifications, user } = useStore();
  const [submitting, setSubmitting] = useState(false);
  const breakpoint = useBreakpoint();
  const itemInCart = cart.get(product.id, price);
  const limited =
    itemInCart &&
    itemInCart.quantity >= price.maxQuantity &&
    !user.isEmployeeMode;

  async function handleClick() {
    if (user.isEmployeeMode) {
      notifications.show({
        type: "modal",
        component: AddToCartModalWithPrices,
        props: {
          product,
        },
      });

      return;
    }

    if (limited) return;

    if (itemInCart && !submitting) {
      setSubmitting(true);
      try {
        await cart.setItemCount(itemInCart, itemInCart.quantity + 1);
        if (!hideCartToast) {
          notifications.show({
            type: "toast",
            component: ProductToast,
            props: {
              item: toJS(itemInCart),
            },
          });
        }
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!submitting) {
      setSubmitting(true);
      try {
        await cart.add(product, price, 1, analyticsMeta, hideCartToast);
        onAddProduct && onAddProduct();
      } finally {
        setSubmitting(false);
      }
    }
  }

  if (limited) {
    return (
      <Tooltip title="Количество ограничено" placement="top" maxWidth={216}>
        <button
          className={cn([
            buttonStyles.button,
            buttonStyles.buttonOrange,
            styles.addToCartSingle,
            limited
              ? styles.addToCartSingleLimited
              : styles.addToCartSingleDefault,
            className,
          ])}
          aria-label="Добавить корзину"
        >
          {["mobile"].includes(breakpoint) ? (
            <AttentionSignSmall data-testid="limited-icon" />
          ) : (
            <AttentionSign data-testid="limited-icon" />
          )}
        </button>
      </Tooltip>
    );
  }

  return (
    <button
      className={cn([
        buttonStyles.button,
        buttonStyles.buttonOrange,
        styles.addToCartSingle,
        styles.addToCartSingleDefault,
        itemInCart && !user.isEmployeeMode && styles.addToCartSingleAccent,
        className,
      ])}
      aria-label="Добавить корзину"
      onClick={handleClick}
    >
      {!itemInCart || user.isEmployeeMode ? (
        <Basket data-testid="cart-icon" />
      ) : (
        <div className={styles.addToCartSinglePlusOne}>+1</div>
      )}
    </button>
  );
};

export default observer(AddToCartSingle);
